export type AuthProvider = "google" | "apple" | "microsoft";
export type PlanName = "basic" | "advanced";

export interface PlanData {
  name: PlanName;
  maxScenarios: number;
}

export class Plan {
  readonly name: PlanName;
  readonly maxScenarios: number;

  private constructor(name: PlanName, maxScenarios: number) {
    this.name = name;
    this.maxScenarios = maxScenarios;
  }

  static fromName(name?: PlanName): Plan {
    if (name === "advanced") {
      return new Plan("advanced", 10);
    }

    return new Plan("basic", 3);
  }

  static fromStoredData(plan?: PlanData | PlanName | null): Plan {
    if (!plan) {
      return Plan.fromName("basic");
    }

    if (typeof plan === "string") {
      return Plan.fromName(plan);
    }

    const planName: PlanName = plan.name === "advanced" ? "advanced" : "basic";
    const defaultPlan = Plan.fromName(planName);

    if (!Number.isInteger(plan.maxScenarios) || plan.maxScenarios < 1) {
      return defaultPlan;
    }

    return defaultPlan.withMaxScenarios(plan.maxScenarios);
  }

  withMaxScenarios(maxScenarios: number): Plan {
    if (!Number.isInteger(maxScenarios) || maxScenarios < 1) {
      return this;
    }

    return new Plan(this.name, maxScenarios);
  }

  toJSON(): PlanData {
    return {
      name: this.name,
      maxScenarios: this.maxScenarios,
    };
  }
}

/**
 * Authenticated user information
 * Minimal information needed to identify authenticated users
 */
export interface AuthUser {
  userId: string;
  email: string;
}

/**
 * User profile data interface (plain data structure for JSON serialization)
 */
export interface UserProfileData {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  provider: AuthProvider;
  providerUserId: string;
  plan?: PlanData | PlanName;
  createdAt: Date;
  lastSignInAt: Date;
  /** Timestamp when user accepted the privacy notice (null = not yet accepted) */
  privacyAcceptedAt?: Date;
}

export interface UserProfileOptions {
  planOverride?: Plan;
}

/**
 * User profile class with methods
 */
export class UserProfile {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  provider: AuthProvider;
  providerUserId: string;
  plan: Plan;
  createdAt: Date;
  lastSignInAt: Date;
  privacyAcceptedAt?: Date;

  constructor(data: UserProfileData, options?: UserProfileOptions) {
    this.userId = data.userId;
    this.email = data.email;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.avatarUrl = data.avatarUrl;
    this.provider = data.provider;
    this.providerUserId = data.providerUserId;
    this.plan = options?.planOverride ?? Plan.fromStoredData(data.plan);
    this.createdAt = data.createdAt;
    this.lastSignInAt = data.lastSignInAt;
    this.privacyAcceptedAt = data.privacyAcceptedAt;
  }

  /**
   * Get user's full name from first and last name
   * @returns Full name as "FirstName LastName"
   */
  fullName(): string {
    return `${this.firstName} ${this.lastName}`.trim();
  }

  /**
   * Get user's initials from their name
   * @returns Two-letter initials in uppercase
   */
  initials(): string {
    const firstInitial = (this.firstName || "").charAt(0).toUpperCase();
    const lastInitial = (this.lastName || "").charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  /**
   * Convert to plain object for JSON serialization
   */
  toJSON(): UserProfileData {
    return {
      userId: this.userId,
      email: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      avatarUrl: this.avatarUrl,
      provider: this.provider,
      providerUserId: this.providerUserId,
      plan: this.plan.toJSON(),
      createdAt: this.createdAt,
      lastSignInAt: this.lastSignInAt,
      privacyAcceptedAt: this.privacyAcceptedAt,
    };
  }

  /**
   * Create UserProfile from plain object
   */
  static fromJSON(data: UserProfileData): UserProfile {
    return new UserProfile(data);
  }
}
